import { motion, AnimatePresence } from "framer-motion";
import { SideSheet, ConfirmDialog, ActivityFeed } from "@/components/ui";
import { RenameDocumentDialog } from "./RenameDocumentDialog";
import { TagSelectorSheet } from "./TagSelectorSheet";
import { useDocumentDetail } from "./hooks/use-document-detail";
import { DocumentDetailHeader } from "./DocumentDetailHeader";
import { DocumentDetailContent } from "./DocumentDetailContent";
import { DocumentDetailLinkedItems } from "./DocumentDetailLinkedItems";
import { DocumentLinkedChecklist } from "./DocumentLinkedChecklist";
import { DocumentDetailFooter } from "./DocumentDetailFooter";
import { DocumentDetailChat } from "./DocumentDetailChat";
import { ChecklistSuggestions } from "./upload/ChecklistSuggestions";
import { useChecklistSuggestions } from "@/hooks/use-checklist-suggestions";
import { useLinkedChecklistItems } from "@/hooks/use-linked-checklist-items";
import type { DocumentView } from "./types";
import type { Id } from "@/packages/types";

interface DocumentDetailSheetProps {
  open: boolean;
  onClose: () => void;
  document: DocumentView | null;
}

type Tab = "details" | "suggestions" | "checklist" | "activity" | "chat";

export function DocumentDetailSheet({
  open,
  onClose,
  document,
}: DocumentDetailSheetProps) {
  const detailState = useDocumentDetail(document);

  const documentId = document?.id ? (document.id as Id<"documents">) : null;
  const { suggestions } = useChecklistSuggestions(documentId);
  const suggestionsCount = suggestions.length;
  const { items: linkedChecklistItems } = useLinkedChecklistItems(documentId);
  const linkedChecklistCount = linkedChecklistItems.length;

  if (!document) return null;

  // Resolve aiSummary with fallback
  const aiSummary =
    document.aiSummary ??
    (document.description
      ? { text: document.description, status: "ready" as const }
      : undefined);

  const handleTagEditorSave = async (tagIds: string[]) => {
    await detailState.handleTagEditorSave(tagIds);
  };

  const handleDelete = async () => {
    const success = await detailState.handleDelete();
    if (success) {
      onClose();
    }
  };

  const handleNavigateToChecklistItem = (checklistItemId: string) => {
    const success = detailState.handleNavigateToChecklistItem(checklistItemId);
    if (success) {
      onClose();
    }
  };

  const tabs: { id: Tab; label: string; count?: number }[] = [
    { id: "details", label: "Details" },
    ...(suggestionsCount > 0
      ? [
          {
            id: "suggestions" as Tab,
            label: "Suggestions",
            count: suggestionsCount,
          },
        ]
      : []),
    {
      id: "checklist" as Tab,
      label: "Checklist",
      count: linkedChecklistCount > 0 ? linkedChecklistCount : undefined,
    },
    {
      id: "activity",
      label: "Activity",
      count:
        detailState.activities.length > 0
          ? detailState.activities.length
          : undefined,
    },
    { id: "chat" as Tab, label: "AI Chat" },
  ];

  return (
    <>
      {/* Hidden file input for version upload */}
      <input
        ref={detailState.fileInputRef}
        type="file"
        className="hidden"
        accept={`.${document.type}`}
        onChange={detailState.handleFileSelected}
      />

      <SideSheet open={open} onClose={onClose}>
        {/* Header */}
        <DocumentDetailHeader
          document={document}
          onUploadNewVersion={detailState.handleUploadNewVersion}
          onRename={() => detailState.setShowRenameDialog(true)}
          onDelete={() => detailState.setShowDeleteConfirm(true)}
          onDownloadPreviousVersion={detailState.downloadPreviousVersion}
          hasPreviousVersion={detailState.hasPreviousVersion}
        />

        {/* Tab bar */}
        <div className="px-6">
          <div className="flex items-center gap-1 border-b border-border">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => detailState.setActiveTab(tab.id)}
                className={`relative px-3 py-2.5 text-[13px] font-medium transition-colors cursor-pointer ${
                  detailState.activeTab === tab.id
                    ? "text-primary"
                    : "text-muted hover:text-secondary"
                }`}
              >
                <span className="flex items-center gap-1.5">
                  {tab.label}
                  {tab.count !== undefined && (
                    <span
                      className={`text-[11px] tabular-nums ${
                        detailState.activeTab === tab.id
                          ? "text-accent"
                          : "text-muted"
                      }`}
                    >
                      {tab.count}
                    </span>
                  )}
                </span>
                {detailState.activeTab === tab.id && (
                  <motion.div
                    layoutId="detail-tab-indicator"
                    className="absolute bottom-0 left-0 right-0 h-[2px] bg-accent rounded-full"
                    transition={{
                      type: "spring",
                      stiffness: 380,
                      damping: 30,
                    }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <SideSheet.Body>
          <AnimatePresence mode="wait">
            {/* Details tab */}
            {detailState.activeTab === "details" && (
              <motion.div
                key="details"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15 }}
                className="space-y-5"
              >
                <DocumentDetailContent
                  document={document}
                  aiSummary={aiSummary}
                  isUploading={detailState.isUploading}
                  viewInNewTab={detailState.viewInNewTab}
                  isDownloadLoading={detailState.isDownloadLoading}
                  onSwitchToSuggestionsTab={
                    suggestionsCount > 0
                      ? () => detailState.setActiveTab("suggestions")
                      : undefined
                  }
                  onCategoryUpdate={detailState.handleCategoryUpdate}
                  onExpirationDateUpdate={
                    detailState.handleExpirationDateUpdate
                  }
                />

                <DocumentDetailLinkedItems
                  documentId={documentId as Id<"documents">}
                  linkedChecklistCount={linkedChecklistCount}
                  onSwitchToChecklistTab={() =>
                    detailState.setActiveTab("checklist")
                  }
                />
              </motion.div>
            )}

            {/* Suggestions tab */}
            {detailState.activeTab === "suggestions" &&
              suggestionsCount > 0 &&
              documentId && (
                <motion.div
                  key="suggestions"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.15 }}
                >
                  <ChecklistSuggestions
                    documentId={documentId}
                    onComplete={() => detailState.setActiveTab("details")}
                  />
                </motion.div>
              )}

            {/* Checklist tab */}
            {detailState.activeTab === "checklist" && documentId && (
              <motion.div
                key="checklist"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15 }}
              >
                <DocumentLinkedChecklist
                  documentId={documentId}
                  onNavigateToItem={handleNavigateToChecklistItem}
                />
              </motion.div>
            )}

            {/* Activity tab */}
            {detailState.activeTab === "activity" && (
              <ActivityFeed
                items={detailState.activities}
                emptyTitle="No activity yet"
                emptyDescription="Activity will appear here as changes are made to this document."
              />
            )}

            {/* AI Chat tab — negative margins cancel SideSheet.Body padding so chat fills edge-to-edge */}
            {detailState.activeTab === "chat" && (
              <motion.div
                key="chat"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15 }}
                className="flex flex-col h-full -mx-6 -mt-4"
              >
                <DocumentDetailChat document={document} />
              </motion.div>
            )}
          </AnimatePresence>
        </SideSheet.Body>

        {/* Footer — hidden on chat tab */}
        {detailState.activeTab !== "chat" && (
          <DocumentDetailFooter
            onDownload={detailState.download}
            isDownloadLoading={detailState.isDownloadLoading}
            authorName={
              detailState.resolvedAuthorName ??
              document.author?.name ??
              "Unknown"
            }
            authorEmail={detailState.resolvedAuthorEmail ?? undefined}
            authorAvatarUrl={document.author?.avatarUrl}
            authorAvatarColor={document.author?.avatarColor}
          />
        )}
      </SideSheet>

      {/* Rename dialog */}
      <RenameDocumentDialog
        open={detailState.showRenameDialog}
        onClose={() => detailState.setShowRenameDialog(false)}
        documentId={document.id}
        currentName={document.title}
      />

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        open={detailState.showDeleteConfirm}
        onClose={() => detailState.setShowDeleteConfirm(false)}
      >
        <ConfirmDialog.Title>Delete document</ConfirmDialog.Title>
        <ConfirmDialog.Body>
          <p className="text-[13px] text-secondary">
            Are you sure you want to delete{" "}
            <span className="font-medium text-primary">{document.title}</span>?
            This action cannot be undone.
          </p>
        </ConfirmDialog.Body>
        <ConfirmDialog.Actions
          onConfirm={handleDelete}
          onCancel={() => detailState.setShowDeleteConfirm(false)}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          variant="danger"
          loading={detailState.isDeleting}
        />
      </ConfirmDialog>

      <TagSelectorSheet
        open={detailState.showTagEditor}
        onClose={() => detailState.setShowTagEditor(false)}
        documentTags={document?.tags}
        documentName={document?.title}
        onSave={handleTagEditorSave}
      />
    </>
  );
}
